/**
 * Resource List configuration.
 *
 * Adds boilerplate configuration to resource list routes, which are identified
 * by the resourceList property.
 *
 * Resource list routes are normal TMR Route objects with some special properties:
 *   url             Must be provided without any query parameters, eg "/users"
 *   resourceType    Name of the service that defines the TMR Resource type, eg "User"
 *   resourceList    Configuration object for the resource list with properties:
 *     - searchParameters: An array of searchable parameters
 *     - hiddenSearchParameters: An array of searchable parameters that will be invisible to the user. Useful
 *         to provide hidden filters or params with fixed values. Users will not see them in the url or in search
 *         summary, but integration with tmrResourceList functionality such as export, reset buttons, polling, etc.
 *         works the same.
 *     - requireFilter: If true, search is performed only if at least one search filter is filled
 *
 * Further customization is available with route params:
 *   - `searchAction` Defaults to `pagedQuery`, use to override the search endpoint
 *   - `countAction` Defaults to `count`, use to override the count endpoint
 *   - `showSearchForm` If true, hide the search form
 *
 * @type {ngConfig}
 */
function tmrResourceListRoutesConfig(
  TmrRoutesProvider,
  TmrPagingProvider,
  tmrDefaultPagingParams
) {
  'ngInject'
  TmrRoutesProvider.afterConfigCallbacks.push(routes => {
    routes.forEach((route, idx) => {
      if (!route.resourceList) return
      if (!route.resourceType)
        throw new Error(
          `The route ${route.name} with a resourceList option is missing the required resourceType option`
        )

      let searchParameters = Object.keys(tmrDefaultPagingParams).concat(
        route.resourceList.searchParameters
      )

      // Don't alter the default route definition: messes up tests.
      routes[idx] = Object.assign(
        {
          paging: true,
          // Set parent menu item to grandparent state.
          mainMenu: 'app.' + route.name.split('.').slice(0, -2).join('.'),
          accessControl: [
            'Resource.list',
            `Resource.${route.resourceType}.list`,
          ],
        },
        route
      )

      routes[idx].params = Object.assign(
        {
          searchAction: { type: 'string', value: 'pagedQuery', squash: true },
          countAction: { type: 'string', value: 'count', squash: true },
          showSearchForm: { type: 'bool', value: false, squash: true },
        },
        route.params
      )

      // hiddenSearchParameters are added as params but not to the url.
      if (route.resourceList.hiddenSearchParameters) {
        route.resourceList.hiddenSearchParameters.forEach(
          param =>
            (routes[idx].params[param] = routes[idx].params[param] ?? null)
        )
      }

      routes[idx].resolve = Object.assign(
        {
          resources: /*@ngInject*/ ($transition$, TmrResourceResolve) =>
            TmrResourceResolve.query($transition$)(),
          resourcesCount: /*@ngInject*/ ($transition$, TmrResourceResolve) =>
            TmrResourceResolve.count($transition$)(),
        },
        route.resolve
      )

      routes[idx].url =
        route.url +
        (route.url.includes('?') ? '&' : '?') +
        searchParameters.join('&')

      // Add paging manually since we may have missed the afterConfigCallbacks.
      TmrPagingProvider.processRoute(routes[idx])
    })
  })
}

export { tmrResourceListRoutesConfig as config }
